/**
 * SzhMappaPage — SZH Mappa (Supplier Credit)
 * Story 62-12: Cash Management Frontend UI
 *
 * Kettos egyenleg, SZH tetel lista, hitelre kiadas, visszafizetes, riport.
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  useCancelCredit,
  useIssueAnonymousCredit,
  useIssueCredit,
  useReserveBalance,
  useSettleCredit,
  useSzhItems,
  useSzhReport,
  useSzhSummary,
  useTodayRegister,
} from '@/hooks/useCashManagement';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function SzhMappaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register } = useTodayRegister();
  const registerId = register?.id ?? '';
  const { items, error: itemsError, refetch } = useSzhItems(registerId);
  const { summary } = useSzhSummary(registerId);
  const { balance: reserveBalance } = useReserveBalance(registerId);
  const issueCredit = useIssueCredit(registerId);
  const issueAnonymous = useIssueAnonymousCredit(registerId);
  const settle = useSettleCredit();
  const cancel = useCancelCredit();
  const report = useSzhReport(registerId);

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [creditForm, setCreditForm] = useState({
    partnerName: '',
    amount: '',
    activity: '',
    isAnonymous: false,
  });

  // Confirmation dialog states
  const [showIssueConfirm, setShowIssueConfirm] = useState(false);
  const [showSettleConfirm, setShowSettleConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [pendingCreditId, setPendingCreditId] = useState<string | null>(null);

  const handleIssueCreditRequest = () => {
    const amount = parseFloat(creditForm.amount);
    if (!amount || amount <= 0 || !creditForm.activity) return;
    setShowIssueConfirm(true);
  };

  const handleIssueCredit = async () => {
    setShowIssueConfirm(false);
    const amount = parseFloat(creditForm.amount);
    if (!amount || amount <= 0 || !creditForm.activity) return;

    if (creditForm.isAnonymous) {
      await issueAnonymous.mutateAsync({ amount, activity: creditForm.activity });
    } else {
      await issueCredit.mutateAsync({
        partnerName: creditForm.partnerName || undefined,
        amount,
        activity: creditForm.activity,
      });
    }

    setCreditForm({ partnerName: '', amount: '', activity: '', isAnonymous: false });
    refetch();
  };

  const handleSettleRequest = (creditId: string) => {
    setPendingCreditId(creditId);
    setShowSettleConfirm(true);
  };

  const handleSettle = async () => {
    if (!pendingCreditId) return;
    setShowSettleConfirm(false);
    await settle.mutateAsync(pendingCreditId);
    setPendingCreditId(null);
    refetch();
  };

  const handleCancelRequest = (creditId: string) => {
    setPendingCreditId(creditId);
    setShowCancelConfirm(true);
  };

  const handleCancel = async () => {
    if (!pendingCreditId) return;
    setShowCancelConfirm(false);
    await cancel.mutateAsync(pendingCreditId);
    setPendingCreditId(null);
    refetch();
  };

  const handleGenerateReport = async () => {
    await report.mutateAsync(undefined);
  };

  const filteredItems = statusFilter ? items.filter(item => item.status === statusFilter) : items;

  if (!register) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground mb-4">{t('finance.noRegisterToClose')}</p>
            <Button onClick={() => navigate('/finance/cash')}>{t('common.back')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('finance.szhFolder')}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleGenerateReport} disabled={report.isPending}>
            {report.isPending ? t('finance.szh.generating') : t('finance.szh.generateReport')}
          </Button>
          <Button variant="outline" onClick={() => navigate('/finance/cash')}>
            {t('common.back')}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-l-4 border-green-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              {t('finance.szh.expectedRevenue')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summary?.expectedRevenue ?? 0)}
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              {t('finance.szh.expectedSettlement')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summary?.expectedSettlement ?? 0)}
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-yellow-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              {t('finance.szh.lossReserve')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(reserveBalance?.netReserve ?? 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Credit form */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('finance.szh.creditIssuance')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Input
              placeholder={t('finance.szh.partnerName')}
              value={creditForm.partnerName}
              onChange={e => setCreditForm({ ...creditForm, partnerName: e.target.value })}
              disabled={creditForm.isAnonymous}
            />
            <Input
              type="number"
              placeholder={t('finance.szh.amountFt')}
              value={creditForm.amount}
              onChange={e => setCreditForm({ ...creditForm, amount: e.target.value })}
            />
            <Input
              placeholder={t('finance.szh.activity')}
              value={creditForm.activity}
              onChange={e => setCreditForm({ ...creditForm, activity: e.target.value })}
            />
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={creditForm.isAnonymous}
                onChange={e => setCreditForm({ ...creditForm, isAnonymous: e.target.checked })}
              />
              <span className="text-sm">{t('finance.szh.anonymous')}</span>
            </label>
            <Button
              onClick={handleIssueCreditRequest}
              disabled={issueCredit.isPending || issueAnonymous.isPending}
            >
              {t('finance.szh.issue')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        {['', 'OPEN', 'SETTLED', 'OVERDUE'].map(status => (
          <Button
            key={status || 'all'}
            variant={statusFilter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(status)}
          >
            {status || t('common.all')}
          </Button>
        ))}
      </div>

      {/* Items list */}
      <Card>
        <CardHeader>
          <CardTitle>{t('finance.szh.items', { count: filteredItems.length })}</CardTitle>
        </CardHeader>
        <CardContent>
          {itemsError ? (
            <div data-testid="szh-error" className="text-center py-4">
              <p className="text-red-600 dark:text-red-400 mb-4">{t('finance.szh.loadError')}</p>
              <Button onClick={() => refetch()}>{t('common.retry')}</Button>
            </div>
          ) : filteredItems.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">{t('finance.szh.noItems')}</p>
          ) : (
            <div className="divide-y">
              {filteredItems.map(item => (
                <div key={item.id} className="py-3 flex justify-between items-center">
                  <div>
                    <div className="font-medium">
                      {item.isAnonymous
                        ? `SZH-ID: ${item.szhId ?? 'N/A'}`
                        : (item.partnerName ?? 'N/A')}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {item.description} — {formatCurrency(item.amount)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(item.createdAt).toLocaleString('hu-HU')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        item.status === 'OPEN'
                          ? 'bg-yellow-100 text-yellow-800'
                          : item.status === 'SETTLED'
                            ? 'bg-green-100 text-green-800'
                            : item.status === 'OVERDUE'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-muted text-foreground'
                      }`}
                    >
                      {item.status}
                    </span>
                    {item.status === 'OPEN' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleSettleRequest(item.id)}
                          disabled={settle.isPending}
                        >
                          {t('finance.szh.repayment')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCancelRequest(item.id)}
                          disabled={cancel.isPending}
                        >
                          {t('finance.szh.reversal')}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SZH Issue Confirmation Dialog */}
      {showIssueConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {t('finance.szh.confirmTitle')}
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-300">{t('finance.szh.confirmBody')}</p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowIssueConfirm(false)}>
                {t('finance.szh.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleIssueCredit}
                disabled={issueCredit.isPending || issueAnonymous.isPending}
              >
                {t('finance.szh.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* SZH Settle Confirmation Dialog */}
      {showSettleConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {t('finance.szh.confirmTitle')}
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-300">{t('finance.szh.confirmBody')}</p>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSettleConfirm(false);
                  setPendingCreditId(null);
                }}
              >
                {t('finance.szh.cancel')}
              </Button>
              <Button variant="destructive" onClick={handleSettle} disabled={settle.isPending}>
                {t('finance.szh.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* SZH Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {t('finance.szh.confirmTitle')}
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-300">{t('finance.szh.confirmBody')}</p>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCancelConfirm(false);
                  setPendingCreditId(null);
                }}
              >
                {t('finance.szh.cancel')}
              </Button>
              <Button variant="destructive" onClick={handleCancel} disabled={cancel.isPending}>
                {t('finance.szh.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
